import type { Subject } from '../common/enums';
import { PermissionType } from './enums';

/** What a permission tag grants, read off its parts. */
export interface PermissionMeaning {
  type: PermissionType;
  subject?: Subject;
  categoryCode?: string;
  topicCode?: string;
  noteCode?: string;
  courseId?: string;
}

/**
 * What a permission tag such as `CPM_VID_CHEM_MOD5_LES4` grants. A tag holds each part
 * upper-cased, so each reads back lower-cased; a tutorial tag splits its course in two.
 */
export const readPermissionTag = (tag: string): PermissionMeaning => {
  const [, typeCode, ...parts] = tag.split('_');

  if (typeCode === undefined) {
    return { type: PermissionType.Base };
  }

  const type = typeCode as PermissionType;
  const [subjectPart, second, third] = parts.map(part => part.toLowerCase());
  const subject = subjectPart as Subject | undefined;

  if (subject === undefined) {
    return { type };
  }
  if (type === PermissionType.Tutorial) {
    return { type, subject, ...(second === undefined ? {} : { courseId: `${subject}${second}` }) };
  }
  if (type === PermissionType.Video || type === PermissionType.Note) {
    const codeField = type === PermissionType.Video ? 'topicCode' : 'noteCode';
    return {
      type,
      subject,
      ...(second === undefined ? {} : { categoryCode: second }),
      ...(third === undefined ? {} : { [codeField]: third }),
    };
  }
  return { type, subject };
};

/** The partition a permission tag is stored under. */
export const getPermissionTagPartition = (tag: string) => {
  const { type } = readPermissionTag(tag);
  return type === PermissionType.Base ? PermissionType.Base : `${PermissionType.Base}_${type}`;
};
